import React, { FC, useRef } from 'react'

import styled from 'styled-components'

import { useCampaignPrice } from 'src/Pages/Artist/SpotifySchedulePage/helpers/helpers'
import { MAX_TABLET_WIDTH, MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { ScheduleBudgetInput } from 'src/Components/ScheduleBudgetInput'
import { PrimaryScheduleSubscription } from 'src/Components/PrimaryScheduleSubscription'
import { SPOTIFY_BUDGET_STEP } from 'src/Constants/constants'
import { PrimarySlider } from 'src/Components/PrimarySlider'

import { SpotifySettingsContinueButton } from './SpotifySettingsContinueButton'
import { SpotifySettingBudgetEstimation } from './SpotifySettingBudgetEstimation'

const Container = styled.div`
  display: flex;
  padding: 24px;
  margin-bottom: 70px;
  flex-direction: column;
  gap: 24px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
  @media screen and (min-width: ${MAX_TABLET_WIDTH}) {
    width: 400px;
  }
  @media screen and (max-width: ${MAX_TABLET_WIDTH}) {
    width: 50%;
  }

  @media screen and (max-width: ${MAX_MOBILE_WIDTH}) {
    width: 100%;
  }
`

const Title = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 150%;
`

const BudgetSliderWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`
const BudgetSliderSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  align-self: flex-start;
  height: auto;
`
const BudgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

export const SpotifySettingsBudget: FC = () => {
  const {
    savePrice,
    rate,
    currencySymbol,
    min,
    max,
    valueInput,
    valueSlider,
    setValueInput,
    setValueSlider,
    loading,
  } = useCampaignPrice()

  const hasUserInteracted = useRef(false)

  const handleSliderChange = (value: number): void => {
    hasUserInteracted.current = true

    const threshold = SPOTIFY_BUDGET_STEP / 2
    const isNearMin = Math.abs(value - min) < threshold
    const isNearMax = Math.abs(value - max) < threshold

    let roundedValue: number
    if (isNearMin) {
      roundedValue = min
    } else if (isNearMax) {
      roundedValue = max
    } else {
      roundedValue =
        Math.round(value / SPOTIFY_BUDGET_STEP) * SPOTIFY_BUDGET_STEP
    }

    setValueInput(Math.round(roundedValue * rate))
    setValueSlider(roundedValue)
  }

  const handleSliderChangeCompleted = (value: number): void => {
    savePrice(value)
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const substr = event.target.value.substring(
      currencySymbol.length,
      event.target.value.length,
    )
    const inputValue = Number(substr)
    if (isNaN(inputValue)) {
      return
    }
    setValueInput(inputValue)

    if (typeof rate === 'number' && rate > 0) {
      const sliderValue = Math.round(inputValue / rate)
      setValueSlider(sliderValue)
      savePrice(sliderValue)
    }
  }

  const handleInputBlur = (): void => {
    if (typeof rate !== 'number' || rate <= 0) {
      return
    }
    const sliderValue = Math.round(valueInput / rate)
    savePrice(sliderValue)
  }

  return (
    <Container>
      <Title>Set up campaign budget</Title>
      <BudgetSliderWrapper>
        <PrimarySlider
          min={min}
          max={max}
          value={valueSlider}
          onChange={handleSliderChange}
          onChangeComplete={handleSliderChangeCompleted}
          step={1}
          formatValue={(val) =>
            `${currencySymbol}${Math.round(val * rate).toLocaleString('en-US')}`
          }
          disabled={loading}
        />

        <BudgetSliderSideContainer>
          <BudgetContainer>
            <ScheduleBudgetInput
              value={`${currencySymbol}${valueInput}`}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
            />
          </BudgetContainer>
          <PrimaryScheduleSubscription
            symbol={currencySymbol}
            price={valueInput}
          />
          <SpotifySettingBudgetEstimation />
        </BudgetSliderSideContainer>
      </BudgetSliderWrapper>
      <SpotifySettingsContinueButton />
    </Container>
  )
}
